import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  IconButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  Switch,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
  EditNote as EditNoteIcon,
  School as SchoolIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  HelpOutline as HelpOutlineIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useOrganizationStore } from '../stores/organization.store';
import { useNotificationStore } from '../stores/notification.store';
import { OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { ESG_NAV_CATALOG } from '../auth';
import oneSignalService from '../services/onesignal.service';
import OrgLogo from './organizations/OrgLogo';
import { Sidebar } from './Sidebar';
import { SetupCoachmark } from './selfSetup/SetupCoachmark';
import { SelfSetupGate } from './selfSetup/SelfSetupGate';
import {
  SelfSetupAwareNavItem,
  SelfSetupNavTourEffects,
} from './selfSetup/SelfSetupAwareNavItem';
import { SelfSetupTourProvider } from './selfSetup/SelfSetupTourProvider';
import DemoBanner from './demo/DemoBanner';
import { isDemoMode } from '../demo/core';
import {
  HELP_GUIDE_APPEARANCE,
  HELP_GUIDE_CHANGE_LANGUAGE,
} from '../constants/knowledgeBaseTryIt';
import { APP_LANGUAGES, setAppLanguage, type AppLanguage } from '../i18n/app';
import { helpI18n } from '../i18n/help';
import { APP_FONT_FAMILY } from '../constants/fonts';
import { APP_SHAPE } from '../constants/colors';
import { resolveOrganizationLogoUrl } from '../helpers/organization.helpers';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';

const drawerWidth = 280;
const collapsedDrawerWidth = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'esg.sidebarCollapsed';
const ACCOUNT_MENU_TRIGGER = 'data-account-menu-trigger';

function findVisibleAccountMenuTrigger(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[${ACCOUNT_MENU_TRIGGER}]`);
  for (const node of Array.from(nodes)) {
    if (node.offsetParent !== null || node.getClientRects().length > 0) {
      return node;
    }
  }
  return nodes[0] ?? null;
}

const LANGUAGE_FONT_FAMILY = APP_FONT_FAMILY;

const NAV_LABEL_KEYS: Record<string, string> = {
  Dashboard: 'dashboard',
  'ESG Reports': 'esgReports',
  'Waste Management': 'wasteManagement',
  'Data Source': 'utilities',
  'Employee training': 'employeeTraining',
  'Health & Safety': 'healthSafety',
};

const NAV_ICONS: Record<string, React.ReactNode> = {
  '/': <DashboardIcon />,
  '/esg-reports': <AssessmentIcon />,
  '/waste-management': <DeleteIcon />,
  '/data-entry': <EditNoteIcon />,
  '/employee-training': <SchoolIcon />,
  '/hse': <HealthAndSafetyIcon />,
};

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [helpAccountGuide, setHelpAccountGuide] = useState<string | null>(null);
  const [languageGuidePhase, setLanguageGuidePhase] = useState<'item' | 'picker'>(
    'item',
  );
  const [languagePickerSettled, setLanguagePickerSettled] = useState(false);
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { unreadCount, fetchNotifications } = useNotificationStore();

  const effectiveDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, accessibleOrgIds, isPlatformAdmin: platformAdmin } = useAuthStore();
  const { organizations, getOrganizations, selectedOrganization, setSelectedOrganization } =
    useOrganizationStore();
  const { filterEsgNavItems } = useAuthPermissions();

  const contentTransition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleSidebarCollapseToggle = useCallback(
    () => setSidebarCollapsed((prev) => !prev),
    [],
  );

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (
      guide !== HELP_GUIDE_CHANGE_LANGUAGE &&
      guide !== HELP_GUIDE_APPEARANCE
    ) {
      return;
    }
    setHelpAccountGuide(guide);
    setLanguageGuidePhase('item');
    markHelpGuideActive(guide);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    setSidebarCollapsed(false);
    if (isMobileViewport) {
      setMobileOpen(true);
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const button = findVisibleAccountMenuTrigger();
        if (button) setAnchorEl(button);
      });
    });
  }, [location.search, location.pathname, navigate, isMobileViewport]);

  const finishAccountGuide = () => {
    setHelpAccountGuide(null);
    setLanguageGuidePhase('item');
    setLanguagePickerSettled(false);
    setAnchorEl(null);
    setLanguageMenuAnchor(null);
    clearHelpGuideActive();
  };

  const showLanguageItemTip =
    helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE &&
    Boolean(anchorEl) &&
    languageGuidePhase === 'item' &&
    !languageMenuAnchor;

  const showAppearanceTip =
    helpAccountGuide === HELP_GUIDE_APPEARANCE && Boolean(anchorEl);

  /** Wait for account Menu Paper to finish positioning before showing tips. */
  const [accountItemTipReady, setAccountItemTipReady] = useState(false);

  const showLanguagePickerTip =
    helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE &&
    Boolean(languageMenuAnchor) &&
    languageGuidePhase === 'picker' &&
    languagePickerSettled;

  useEffect(() => {
    if (!showLanguageItemTip && !showAppearanceTip) {
      setAccountItemTipReady(false);
      return;
    }
    setAccountItemTipReady(false);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!cancelled) setAccountItemTipReady(true);
        });
      });
    }, 40);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [showLanguageItemTip, showAppearanceTip, anchorEl]);

  useEffect(() => {
    if (
      helpAccountGuide !== HELP_GUIDE_CHANGE_LANGUAGE ||
      languageGuidePhase !== 'picker' ||
      !languageMenuAnchor
    ) {
      setLanguagePickerSettled(false);
      return;
    }
    setLanguagePickerSettled(false);
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setLanguagePickerSettled(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [helpAccountGuide, languageGuidePhase, languageMenuAnchor]);

  const navLabel = (text: string) => t(`nav.${NAV_LABEL_KEYS[text] ?? text}`, text);
  const navIconFor = (path: string) => NAV_ICONS[path] ?? <DashboardIcon />;

  const orgOptions = useMemo(() => {
    if (platformAdmin || accessibleOrgIds.length === 0) {
      return organizations;
    }
    const allowed = new Set(accessibleOrgIds);
    return organizations.filter((o) => allowed.has(o.id));
  }, [organizations, accessibleOrgIds, platformAdmin]);

  useEffect(() => {
    const savedOrg = sessionStorage.getItem('selectedOrganization');
    if (savedOrg) {
      try {
        const parsedOrg = JSON.parse(savedOrg) as OrganizationSchema;
        if (
          platformAdmin ||
          accessibleOrgIds.length === 0 ||
          accessibleOrgIds.includes(parsedOrg.id)
        ) {
          setSelectedOrganization(parsedOrg);
        } else {
          sessionStorage.removeItem('selectedOrganization');
        }
      } catch (error) {
        console.error('Error parsing saved organization:', error);
      }
    }
  }, [accessibleOrgIds, platformAdmin, setSelectedOrganization]);

  const fetchOrganizations = useCallback(
    async (page = 1, input = '') => {
      const params = { page, limit: 10, name: input || undefined };
      await getOrganizations(params);
    },
    [getOrganizations],
  );

  useEffect(() => {
    fetchOrganizations(1, searchQuery);
  }, [fetchOrganizations, searchQuery]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleProfileMenuOpen = () => {
    setAnchorEl(findVisibleAccountMenuTrigger());
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    if (helpAccountGuide) {
      finishAccountGuide();
    }
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      setLanguageGuidePhase('picker');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const el = document.getElementById('help-account-languages');
          if (el) setLanguageMenuAnchor(el);
        });
      });
      return;
    }
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      finishAccountGuide();
    }
  };

  const handleLanguageSelect = (language: AppLanguage) => {
    setAppLanguage(language);
    setLanguageMenuAnchor(null);
    setAnchorEl(null);
    if (helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE) {
      finishAccountGuide();
    }
  };

  const handleOrganizationChange = async (
    _: unknown,
    newValue: OrganizationSchema | null,
  ) => {
    if (newValue) {
      setSelectedOrganization(newValue);
      sessionStorage.setItem('selectedOrganization', JSON.stringify(newValue));

      if (user && !user.data?.onesignal_player_id && !isDemoMode()) {
        try {
          if (oneSignalService.isReady()) {
            await oneSignalService.registerPlayerWithBackend(newValue.id, user.sub);
            await oneSignalService.setExternalUserId(user.sub);
          }
        } catch (error) {
          console.error('Error registering OneSignal player for new organization:', error);
        }
      }

      window.location.reload();
    }
  };

  useEffect(() => {
    // No push registration in the public demo.
    if (isDemoMode()) return;
    const registerOneSignalForOrganization = async () => {
      if (selectedOrganization && user) {
        if (user.data?.onesignal_player_id) {
          return;
        }
        try {
          if (oneSignalService.isReady()) {
            await oneSignalService.registerPlayerWithBackend(
              selectedOrganization.id,
              user.sub,
            );
            await oneSignalService.setExternalUserId(user.sub);
          }
        } catch (error) {
          console.error('Error registering OneSignal player:', error);
        }
      }
    };

    const timer = setTimeout(registerOneSignalForOrganization, 2000);
    return () => clearTimeout(timer);
  }, [selectedOrganization, user]);

  useEffect(() => {
    if (!selectedOrganization?.id) return;
    void fetchNotifications(selectedOrganization.id, { limit: 20 });
    const intervalId = window.setInterval(() => {
      void fetchNotifications(selectedOrganization.id, { limit: 20 });
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [selectedOrganization?.id, fetchNotifications]);

  const visibleMenuItems = useMemo(() => {
    if (!selectedOrganization) {
      return [];
    }
    return filterEsgNavItems(ESG_NAV_CATALOG, selectedOrganization.organization_type);
  }, [selectedOrganization, filterEsgNavItems]);

  const logoSrc =
    theme.palette.mode === 'dark' ? '/images/esg_dark.png' : '/images/esg_light.png';

  const orgLogoUrl = resolveOrganizationLogoUrl(selectedOrganization);

  const isNavSelected = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const revealSidebarForNavTour = useCallback(() => {
    setSidebarCollapsed(false);
    if (isMobileViewport) setMobileOpen(true);
  }, [isMobileViewport]);

  const renderNavItem = (text: string, path: string) => (
    <SelfSetupAwareNavItem key={path} path={path}>
      <Sidebar.NavItem
        icon={navIconFor(path)}
        label={navLabel(text)}
        selected={isNavSelected(path)}
        onClick={() => navigate(path)}
      />
    </SelfSetupAwareNavItem>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Sidebar.Header
        collapsedChildren={
          <Box
            aria-label={t('layout.brandAria')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={logoSrc}
                  alt={t('layout.brandAria')}
                  sx={{
                    height: '28.75px',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'block',
                    ml: '-0.41px',
                    mt: '-0.13px',
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.66rem',
                letterSpacing: '0.06em',
                lineHeight: 1,
                color: theme.palette.primary.main,
              }}
            >
              ESG
            </Typography>
          </Box>
        }
      >
        <img
          src={logoSrc}
          alt={t('layout.brandAria')}
          style={{ height: 44, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Sidebar.Header>

      <Sidebar.ExpandedOnly>
        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <Autocomplete
            value={selectedOrganization}
            onChange={handleOrganizationChange}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            options={orgOptions}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common.organization')}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: `${theme.palette.primary.main}40` },
                    '&:hover fieldset': { borderColor: `${theme.palette.primary.main}80` },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                  },
                  '& .MuiInputLabel-root': { color: `${theme.palette.primary.main}80` },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                }}
              />
            )}
            fullWidth
          />
        </Box>
        <Divider />
      </Sidebar.ExpandedOnly>

      <Sidebar.Nav>
        {visibleMenuItems.map((item) => renderNavItem(item.text, item.path))}

        {selectedOrganization && visibleMenuItems.length === 0 && (
          <Sidebar.ExpandedOnly>
            <Typography
              variant="caption"
              sx={{ p: 2, display: 'block', textAlign: 'center', color: 'text.secondary' }}
            >
              {t('nav.noModules', 'No modules available')}
            </Typography>
          </Sidebar.ExpandedOnly>
        )}
      </Sidebar.Nav>

      <Box sx={{ flexGrow: 1 }} />

      <Sidebar.ExpandedOnly>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        {selectedOrganization && (
          <Sidebar.NavItem
            icon={
              <Badge color="error" badgeContent={unreadCount} overlap="circular">
                <NotificationsIcon />
              </Badge>
            }
            label={t('nav.notifications')}
            selected={location.pathname === '/notifications'}
            onClick={() => navigate('/notifications')}
          />
        )}
        <Sidebar.NavItem
          icon={<HelpOutlineIcon />}
          label={t('nav.help')}
          selected={location.pathname.startsWith('/knowledge-base')}
          onClick={() => navigate('/knowledge-base')}
        />
        <Sidebar.NavItem
          icon={
            <OrgLogo
              name={selectedOrganization?.name ?? user?.username ?? t('account.title')}
              logoUrl={orgLogoUrl}
              size={28}
            />
          }
          label={user?.username ?? t('account.title')}
          selected={Boolean(anchorEl)}
          onClick={handleProfileMenuOpen}
          buttonProps={{ [ACCOUNT_MENU_TRIGGER]: 'true' }}
        />
      </Sidebar.Nav>

      <Sidebar.CollapseToggle onClick={handleSidebarCollapseToggle} />
    </Box>
  );

  return (
    <SelfSetupTourProvider>
    <SelfSetupNavTourEffects onRevealSidebar={revealSidebarForNavTour} />
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label={t('layout.openDrawer')}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      <Sidebar
        width={drawerWidth}
        collapsedWidth={collapsedDrawerWidth}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
      >
        {drawer}
      </Sidebar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ display: { xs: 'flex', sm: 'none' } }} />
        <DemoBanner />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: `${APP_SHAPE.radiusLg}px`,
            mt: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            ml: { xs: 1.5, sm: 0 },
          }}
        >
          {!isDemoMode() && <SelfSetupGate />}
          {children}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        hideBackdrop={
          (showLanguageItemTip && accountItemTipReady) ||
          (showAppearanceTip && accountItemTipReady)
        }
        transitionDuration={
          showLanguageItemTip || showAppearanceTip ? 0 : undefined
        }
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        marginThreshold={8}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <OrgLogo
            name={selectedOrganization?.name ?? user?.username ?? t('account.title')}
            logoUrl={orgLogoUrl}
            size={36}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {user?.username}
            </Typography>
            {selectedOrganization && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedOrganization.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <SetupCoachmark
          open={showAppearanceTip && accountItemTipReady}
          title={tHelp('knowledgeBase.appearanceItemTitle')}
          subtitle={tHelp('knowledgeBase.appearanceItemSubtitle')}
          onDismiss={finishAccountGuide}
          onNext={finishAccountGuide}
          nextLabel={t('selfSetup.tourDone')}
          fullWidth
          compact
          placement="right"
        >
          <MenuItem
            id="help-account-appearance"
            onClick={() => {
              onThemeToggle();
              if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                finishAccountGuide();
              }
            }}
            sx={{
              transition: 'all 0.2s ease-in-out',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              {theme.palette.mode === 'dark' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={t('account.darkMode')} />
            <Switch
              edge="end"
              size="small"
              checked={theme.palette.mode === 'dark'}
              onClick={(event) => event.stopPropagation()}
              onChange={() => {
                onThemeToggle();
                if (helpAccountGuide === HELP_GUIDE_APPEARANCE) {
                  finishAccountGuide();
                }
              }}
            />
          </MenuItem>
        </SetupCoachmark>
        <SetupCoachmark
          open={showLanguageItemTip && accountItemTipReady}
          title={tHelp('knowledgeBase.changeLanguageItemTitle')}
          subtitle={tHelp('knowledgeBase.changeLanguageItemSubtitle')}
          onDismiss={finishAccountGuide}
          fullWidth
          compact
          placement="right"
        >
          <MenuItem
            id="help-account-languages"
            onClick={handleLanguageMenuOpen}
            sx={{
              transition: 'all 0.2s ease-in-out',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
            }}
          >
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('account.languages')} />
            <ChevronRightIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          </MenuItem>
        </SetupCoachmark>
        <Divider />
        <MenuItem
          onClick={() => {
            void logout();
          }}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.error.main}10` },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          {t('account.logout')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
        hideBackdrop={showLanguagePickerTip}
        transitionDuration={
          helpAccountGuide === HELP_GUIDE_CHANGE_LANGUAGE ? 0 : undefined
        }
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 240,
            maxHeight: 420,
            py: 0.5,
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <SetupCoachmark
          open={showLanguagePickerTip}
          title={tHelp('knowledgeBase.changeLanguagePickerTitle')}
          subtitle={tHelp('knowledgeBase.changeLanguagePickerSubtitle')}
          onDismiss={finishAccountGuide}
          onNext={finishAccountGuide}
          nextLabel={t('selfSetup.tourDone')}
          fullWidth
          placement="right"
        >
          <Box id="help-account-language-picker">
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 2,
                py: 0.5,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.08em',
              }}
            >
              {t('account.languages')}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            {APP_LANGUAGES.map((language) => {
              const isActive = i18n.language.startsWith(language.code);
              const localizedName = t(
                `languages.${language.englishLabel.toLowerCase()}`,
                language.englishLabel,
              );
              const showSecondary = localizedName !== language.nativeLabel;
              return (
                <MenuItem
                  key={language.code}
                  selected={isActive}
                  onClick={() => handleLanguageSelect(language.code)}
                  dir="ltr"
                  sx={{
                    gap: 1.25,
                    minHeight: 48,
                    py: 0.75,
                    px: 1.25,
                    mx: 0.75,
                    borderRadius: '10px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
                    '&.Mui-selected': {
                      bgcolor: `${theme.palette.primary.main}14`,
                      '&:hover': { bgcolor: `${theme.palette.primary.main}1F` },
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      bgcolor: isActive
                        ? theme.palette.primary.main
                        : theme.palette.action.hover,
                      color: isActive
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.secondary,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {language.code.toUpperCase()}
                  </Box>
                  <ListItemText
                    primary={language.nativeLabel}
                    secondary={showSecondary ? localizedName : undefined}
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: LANGUAGE_FONT_FAMILY,
                        fontSize: '0.95rem',
                        fontWeight: isActive ? 700 : 500,
                        lineHeight: 1.35,
                        textAlign: 'start',
                      },
                      dir: 'ltr',
                    }}
                    secondaryTypographyProps={{
                      sx: {
                        fontFamily: LANGUAGE_FONT_FAMILY,
                        fontSize: '0.72rem',
                        lineHeight: 1.3,
                        textAlign: 'start',
                      },
                      dir: 'ltr',
                    }}
                  />
                  {isActive && (
                    <CheckIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                  )}
                </MenuItem>
              );
            })}
          </Box>
        </SetupCoachmark>
      </Menu>
    </Box>
    </SelfSetupTourProvider>
  );
};

export default Layout;
